import {inject, Injectable} from '@angular/core';
import {EnvironmentService} from '../environment.service';
import {Observable} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {IUsageStatsExportCSVRequest} from '../../common/models/interfaces';
import {BaseProduct} from '../../types/product.type';
import {ElementSettings, TuningsResponse} from '../../types/tunings';
import {ApiClient} from './userway-api.service';
import {
  ApiCodeResponse,
  ChangePasswordBodyDto,
  EnableTrialBodyDto,
  GetAccountManagerResponseDto,
  IntercomUserDetailsListData,
  RemediationTranslateQuotaListData,
  TuningsUpdatePersiteCreateData,
  UpdatePaidPersiteTuningsRequestDto,
  UwV2DashboardProductsUsageStatsListParams,
  UwV2DashboardProductsUsageStatsLstExportCsvListData,
  UwV2DashboardProductsUsageStatsLstExportCsvListParams,
  UwV2ImageAltsQuotaListData,
  V1BillingProductsNonSiteDetailData,
  V1BillingStripeSubscriptionPayOnTrialCreateData,
  V1SiteAccessibilityStatementsDetailData,
  V1SplGetOrCreateDetailParams,
} from '../../../userway/sdk/UserWayApiClient';
import {
  DisableIpWhitelistingData,
  GetUserInfoData,
  SetIpWhitelistingData,
  UpdateIpWhitelistDto,
  ConfirmEmailMfaData,
  ConfirmEmailMfaDto,
  ConfirmVirtualMfaData,
  ConfirmVirtualMfaDto,
  DisableEmailMfaData,
  DisableVirtualMfaData,
  GetAccountMfaInfoData,
  InitializeEmailMfaData,
  InitializeVirtualMfaData,
  GetUnitInfoData,
} from '../../../userway/sdk/SSOApiClient';
import {ResetPassword1Data} from '../../../userway/sdk/SSOApiClient';

@Injectable({
  providedIn: 'root',
})
export class SettingsApiService {
  private httpSkipHeaders: HttpHeaders = new HttpHeaders({skip: 'true'});

  apiClient = inject(ApiClient);
  env = inject(EnvironmentService);
  http = inject(HttpClient);

  changePassword(password: ChangePasswordBodyDto): Observable<ApiCodeResponse> {
    const request = this.apiClient.api.auth.authProfilePasswordUpdate(password);
    return this.apiClient.fetch(request);
  }

  updateSettingsPersite(settings: ElementSettings): Observable<TuningsUpdatePersiteCreateData> {
    const request = this.apiClient.api.tunings.tuningsUpdatePersiteCreate(settings);
    return this.apiClient.fetch(request);
  }

  updateSettingsPersitePaid(settings: UpdatePaidPersiteTuningsRequestDto): Observable<any> {
    const request = this.apiClient.api.tunings.tuningsUpdatePersitePaidCreate(settings);
    return this.apiClient.fetch(request);
  }

  updateAccStatement(currentSiteId: number, data: any): Observable<any> {
    const url = `${this.env.apiUrl.apiBaseV1}site-accessibility-statements/${currentSiteId}`;
    return this.http.post(url, data);
  }

  getAccStatement(currentSiteId: number): Observable<V1SiteAccessibilityStatementsDetailData> {
    const request = this.apiClient.api.a11YStatements.v1SiteAccessibilityStatementsDetail(currentSiteId.toString());
    return this.apiClient.fetch(request);
  }

  getSettings(site: {origin: string; ignore?: boolean}, code: string) {
    const url = `${this.env.apiUrl.apiBase}tunings-mc/${code}`;

    return this.http.post<TuningsResponse>(url, site, {headers: this.httpSkipHeaders});
  }

  enableTrial(service: EnableTrialBodyDto): Observable<any> {
    const request = this.apiClient.api.paidServices.servicesEnableTrialCreate(service);
    return this.apiClient.fetch(request);
  }

  getWidgetStats(currentUrl: string) {
    const url = `${this.env.apiUrl.apiBase}a11y-data/v0/site/${encodeURIComponent(
      currentUrl,
    )}/stats?computeMissing=false`;
    return this.http.get(url);
  }

  getProductPaidData<T = BaseProduct>(siteId: number, productName: string) {
    const url = `${this.env.apiUrl.apiBaseV1}sites/${siteId}/products/${productName}`;

    return this.http.get<T>(url);
  }

  getProduct(productName: string): Observable<V1BillingProductsNonSiteDetailData> {
    const request = this.apiClient.api.products.v1BillingProductsNonSiteDetail(productName);
    return this.apiClient.fetch(request);
  }

  getUsageStats(statsParams: UwV2DashboardProductsUsageStatsListParams): Observable<any> {
    const request = this.apiClient.api.products.uwV2DashboardProductsUsageStatsList(statsParams);
    return this.apiClient.fetch(request);
  }

  exportUsageStatsInCsv(exportStatsParams: IUsageStatsExportCSVRequest) {
    const request = this.apiClient.api.products.uwV2DashboardProductsUsageStatsExportCsvList(exportStatsParams);
    return this.apiClient.fetch(request);
  }

  exportLstStatsInCsv(
    exportStatsParams: UwV2DashboardProductsUsageStatsLstExportCsvListParams,
  ): Observable<UwV2DashboardProductsUsageStatsLstExportCsvListData> {
    const request = this.apiClient.api.products.uwV2DashboardProductsUsageStatsLstExportCsvList(exportStatsParams);
    return this.apiClient.fetch(request);
  }

  switchToAnnual(userServiceId: number) {
    const request = this.apiClient.api.subscriptions.v1BillingStripeSubscriptionSwitchToAnnualCreate(
      userServiceId.toString(),
    );
    return this.apiClient.fetch(request);
  }

  getProductPaidDataAll(siteId: number) {
    const url = `${this.env.apiUrl.apiBaseV1}sites/${siteId}/products`;
    return this.http.get(url);
  }

  getProducts(siteId: number) {
    const url = `${this.env.apiUrl.apiBaseV1}agency-account/site/${siteId}`;
    return this.http.get(url);
  }

  payOnTrial(userServiceId: number) {
    const request = this.apiClient.api.billing.v1BillingSubscriptionsPayOnTrialCreate(userServiceId.toString());
    return this.apiClient.fetch(request);
  }

  paySubscriptionOnTrial(subscriptionId: number): Observable<V1BillingStripeSubscriptionPayOnTrialCreateData> {
    const request = this.apiClient.api.subscriptions.v1BillingStripeSubscriptionPayOnTrialCreate(
      subscriptionId.toString(),
    );
    return this.apiClient.fetch(request);
  }

  generatePaymentUrl(splData: V1SplGetOrCreateDetailParams) {
    const request = this.apiClient.api.spl.v1SplGetOrCreateDetail({
      campaign: splData.campaign,
      accountUuid: splData.accountUuid,
    });

    return this.apiClient.fetch(request);
  }

  oauthExchange(authCode: string) {
    const headers = this.apiClient.createAxiosCustomHeaderContext({
      Authorization: 'AuthCode ' + authCode,
    });

    const request = this.apiClient.ssoApi.api.ssoV0UserLoginOauthExchangeList({
      headers,
    });

    return this.apiClient.fetch(request);
  }

  oauthRecoveryCodeExchange(recoveryCode: string): Observable<ResetPassword1Data> {
    const request = this.apiClient.ssoApi.serviceUserController.resetPassword1({
      resetPasswordCode: recoveryCode,
    });
    return this.apiClient.fetch(request);
  }

  getExtendedTokenData() {
    const request = this.apiClient.ssoApi.extendedJwtController.getExtendedJwtUserDetails();
    return this.apiClient.fetch(request);
  }

  updateModifyMenuDetails(siteId: number, data: any): Observable<ApiCodeResponse> {
    const request = data?.allSites
      ? this.apiClient.api.paidServices.servicesUpdateDetailsGlobalCreate(data)
      : this.apiClient.api.paidServices.servicesUpdateDetailsCreate(String(siteId), data);

    return this.apiClient.fetch(request);
  }

  getUserDataIntercom(): Observable<IntercomUserDetailsListData> {
    const request = this.apiClient.api.misc.intercomUserDetailsList();
    return this.apiClient.fetch(request);
  }

  getLiveTranslationsStats(siteId: number): Observable<any> {
    const url = `${this.env.apiUrl.apiBase}usage-stats/v0/user/live-translation/${siteId}/mc-stats`;
    return this.http.get(url);
  }

  getLiveTranslationsQuota(siteId: string): Observable<RemediationTranslateQuotaListData> {
    const request = this.apiClient.api.liveTranslation.remediationTranslateQuotaList({siteId});
    return this.apiClient.fetch(request);
  }

  getImagesAlt(data: any, query?: any): Observable<any> {
    let url = `${this.env.apiUrl.apiBase}img-dscr/v0/remediation/missing-alt/get`;
    if (query) {
      url += '?' + query;
    }
    return this.http.post(url, data);
  }

  getImagesAltQuota(siteId: string): Observable<UwV2ImageAltsQuotaListData> {
    const request = this.apiClient.api.imageAlts.uwV2ImageAltsQuotaList({siteId});
    return this.apiClient.fetch(request);
  }

  getNumbers(data: any): Observable<any> {
    const url = `${this.env.apiUrl.apiBase}img-dscr/v0/remediation/missing-alt/numbers`;
    return this.http.post(url, data);
  }

  toggleDecorative(data: any): Observable<any> {
    const url = `${this.env.apiUrl.apiBase}img-dscr/v0/remediation/missing-alt/toggle-decorative`;
    return this.http.post(url, data);
  }

  updateMissingAlt(data: any): Observable<any> {
    const url = `${this.env.apiUrl.apiBase}img-dscr/v0/remediation/missing-alt/update`;
    return this.http.post(url, data);
  }

  revertAlt(data: any): Observable<any> {
    const url = `${this.env.apiUrl.apiBase}img-dscr/v0/remediation/missing-alt/revert`;
    return this.http.post(url, data);
  }

  getWhitelistIPs(): Observable<GetUserInfoData> {
    const request = this.apiClient.ssoApi.serviceUserController.getUserInfo();
    return this.apiClient.fetch(request);
  }

  addWhitelistIp(data: UpdateIpWhitelistDto): Observable<SetIpWhitelistingData> {
    const request = this.apiClient.ssoApi.serviceUserIpWhitelistController.setIpWhitelisting(data);
    return this.apiClient.fetch(request);
  }

  deleteWhitelistIp(): Observable<DisableIpWhitelistingData> {
    const request = this.apiClient.ssoApi.serviceUserIpWhitelistController.disableIpWhitelisting();
    return this.apiClient.fetch(request);
  }

  getMfa(): Observable<GetAccountMfaInfoData> {
    const request = this.apiClient.ssoApi.mfaController.getAccountMfaInfo();
    return this.apiClient.fetch(request);
  }

  getMfaVirtual(): Observable<InitializeVirtualMfaData> {
    const request = this.apiClient.ssoApi.mfaVirtualController.initializeVirtualMfa();
    return this.apiClient.fetch(request);
  }

  getMfaEmail(): Observable<InitializeEmailMfaData> {
    const request = this.apiClient.ssoApi.mfaEmailController.initializeEmailMfa();
    return this.apiClient.fetch(request);
  }

  setMfaVirtual(data: ConfirmVirtualMfaDto): Observable<ConfirmVirtualMfaData> {
    const request = this.apiClient.ssoApi.mfaVirtualController.confirmVirtualMfa(data);
    return this.apiClient.fetch(request);
  }

  setMfaEmail(data: ConfirmEmailMfaDto): Observable<ConfirmEmailMfaData> {
    const request = this.apiClient.ssoApi.mfaEmailController.confirmEmailMfa(data);
    return this.apiClient.fetch(request);
  }

  removeMfaVirtual(): Observable<DisableVirtualMfaData> {
    const request = this.apiClient.ssoApi.mfaVirtualController.disableVirtualMfa();
    return this.apiClient.fetch(request);
  }

  removeMfaEmail(): Observable<DisableEmailMfaData> {
    const request = this.apiClient.ssoApi.mfaEmailController.disableEmailMfa();
    return this.apiClient.fetch(request);
  }

  getScannerLink(): Observable<any> {
    const url = `${this.env.apiUrl.apiBase}apps/manage-console/callback/auth`;
    return this.http.get(url);
  }

  getAccountManager(): Observable<GetAccountManagerResponseDto> {
    const request = this.apiClient.api.agency.uwV2AgencyAccountManagerList();
    return this.apiClient.fetch(request);
  }

  getSsoUnitInfo(identifier: string): Observable<GetUnitInfoData> {
    const request = this.apiClient.ssoApi.unitController.getUnitInfo(identifier);
    return this.apiClient.fetch(request);
  }
}
